import { mediaQueryLarge, isMobileBreakpoint } from "@theme/utilities";

/**
 * Remembers which accordions the user has explicitly opened/closed, keyed by
 * section + surface (drawer vs. inline pane) + filter param name, so that a
 * section re-render (e.g. after applying a filter) doesn't fight the user's
 * choice by falling back to the server-computed default open state.
 * @type {Map<string, boolean>}
 */
const userOpenState = new Map();

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector("details");

    if (!(details instanceof HTMLDetailsElement)) throw new Error("Details element not found");

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector("summary");

    if (!(summary instanceof HTMLElement)) throw new Error("Summary element not found");

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === "true";
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === "true";
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === "true";
  }

  /**
   * Key used to remember this accordion's open state across section re-renders, or `null` if
   * this accordion isn't a facet filter (only filters opt into persisting user-toggled state).
   * @returns {string | null}
   */
  get #stateKey() {
    const filterParamName = this.dataset.filterParamName;
    if (!filterParamName) return null;

    const sectionId = this.closest(".shopify-section")?.id ?? "";
    const surface = this.closest("dialog") ? "drawer" : "pane";

    return `${sectionId}:${surface}:${filterParamName}`;
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#applyOpenState();

    this.addEventListener("keydown", this.#handleKeyDown, { signal });
    this.summary.addEventListener("click", this.handleClick, { signal });
    this.details.addEventListener("toggle", this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event. Leaves accordions the user has already toggled alone.
   */
  #handleMediaQueryChange = () => {
    const key = this.#stateKey;
    if (key && userOpenState.has(key)) return;

    this.#setDefaultOpenState();
  };

  /**
   * Records the accordion's open state whenever it changes, so it can be restored later
   * regardless of what caused the change (user click, keyboard, or our own default-state logic).
   */
  #handleToggle = () => {
    const key = this.#stateKey;
    if (key) userOpenState.set(key, this.details.open);
  };

  /**
   * Applies the accordion's open state on connect: the user's last explicit choice for this
   * filter, if any, otherwise the server-computed default.
   */
  #applyOpenState() {
    const key = this.#stateKey;
    const rememberedState = key ? userOpenState.get(key) : undefined;

    if (rememberedState !== undefined) {
      this.details.open = rememberedState;
      return;
    }

    this.#setDefaultOpenState();
  }

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute("open-by-default-on-mobile")) ||
      (!isMobile && this.hasAttribute("open-by-default-on-desktop"));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === "Escape" && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get("accordion-custom")) {
  customElements.define("accordion-custom", AccordionCustom);
}
